<script setup lang="ts">
import { api } from '@/api';
import { NumericTrackItDataTypeFilterDto } from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import SearchField from '@/components/filter/SearchField.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import Loader from '@/components/Loader/Loader.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import { truthy } from '@/utils/helper';
import { I18nKey } from '@/utils/typings';
import { useTranslation } from 'i18next-vue';
import { uniqBy } from 'lodash';
import { computed, ref, watch } from 'vue';
import {
  describeNumericTrackItDataTypeFilter,
  numericTrackItDataTypeKind,
} from './trackItDataTypeFilters';

const modelValue = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  apply: [filter: NumericTrackItDataTypeFilterDto];
}>();

const { t } = useTranslation();

const headers = [
  {
    title: '',
    sortable: false,
    width: '40px',
    key: 'selected',
  },
  {
    title: t('Name'),
    key: 'name',
    sortable: false,
  },
  {
    title: t('Unit_one'),
    key: 'unit',
    sortable: false,
  },
  {
    title: t('ValueType'),
    key: 'valueType',
    sortable: false,
  },
] as const;

const suggestions = ref<NumericTrackItDataTypeFilterDto[]>([]);
const loading = ref(false);
const selectedKey = ref<string>();
const keywordFilter = ref('');

// Keyed by the full label, so filters known from several evaluation parameters show up once.
const rows = computed(() =>
  uniqBy(
    suggestions.value
      .filter((filter) => filter.dataType?.name)
      .map((filter) => ({
        key: describeNumericTrackItDataTypeFilter(filter),
        name: filter.dataType?.name ?? '',
        definition: filter.dataType?.definition ?? undefined,
        unit: filter.dataType?.unit ?? undefined,
        valueType: filter.dataType ? numericTrackItDataTypeKind(filter.dataType) : '',
        filter,
      })),
    'key',
  ),
);

const filteredRows = computed(() => {
  const keyword = keywordFilter.value?.trim().toLowerCase();
  if (!keyword) return rows.value;

  return rows.value.filter((row) =>
    [row.name, row.definition, row.unit, row.valueType]
      .filter(truthy)
      .some((value) => value.toLowerCase().includes(keyword)),
  );
});

watch(modelValue, async (open) => {
  if (!open) return;

  selectedKey.value = undefined;
  keywordFilter.value = '';
  loading.value = true;
  const { result, success } = await api.trackItImportApi.getSuggestedTrackItDataTypeFilters();
  loading.value = false;
  suggestions.value = success ? (result ?? []) : [];
});

function close() {
  modelValue.value = false;
}

function apply() {
  const row = rows.value.find(({ key }) => key === selectedKey.value);
  if (row) {
    emit('apply', row.filter);
  }
  close();
}
</script>

<template>
  <Dialog :modelValue="modelValue" size="large" @update:modelValue="close">
    <template #title>
      <span>{{ $t('SelectTrackItFilter' as I18nKey) }}</span>
    </template>

    <template #text>
      <Loader v-if="loading" type="content" />

      <v-radio-group v-else v-model="selectedKey" hideDetails>
        <SearchField
          v-model="keywordFilter"
          :placeholder="$t('SearchPlaceholder')"
          class="w-33 mb-4"
        />

        <OverviewTable
          :key="filteredRows.length"
          :items="filteredRows"
          :showItemsCount="true"
          :zebra="true"
          :noSpacing="true"
          :headers="headers"
          @click:row="(_event, data) => (selectedKey = data.item.key)"
        >
          <template #[`item.selected`]="{ item }">
            <v-radio :value="item.key" />
          </template>

          <template #[`item.name`]="{ item }">
            <span class="font-weight-bold">{{ item.name }}</span>
            <div v-if="item.definition" class="text-caption text-medium-emphasis">
              {{ item.definition }}
            </div>
          </template>

          <template #[`item.unit`]="{ item }">
            {{ item.unit || EMPTY_FIELD_SIGN }}
          </template>
        </OverviewTable>
      </v-radio-group>
    </template>

    <template #actions>
      <Button variant="text" :label="$t('Cancel')" @click="close()" />
      <Button variant="secondary" :label="$t('Apply')" :disabled="!selectedKey" @click="apply()" />
    </template>
  </Dialog>
</template>
